import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { SeasonRoster, UserProfile } from './types';
import { fetchUserProfile, fetchRosters, ApiError, setActiveUser } from './utils/apiClient';

interface RosterContextType {
    rosters: Record<string, SeasonRoster>;
    isLoading: boolean;
    error: string | null;
    isAuthorized: boolean | null; // null: checking/initial, true: allowed, false: denied
    isTeamManager: boolean;
    isAdmin: boolean;
    userProfile: UserProfile | null;
    isAuthReady: boolean;
    getRoster: (seasonKey: string) => SeasonRoster | null;
    refetchRosters: () => Promise<void>;
}

const RosterContext = createContext<RosterContextType | undefined>(undefined);

export const STORAGE_KEY_ROSTERS = 'chicagojets_rosters_cache';
export const STORAGE_KEY_PROFILE_PREFIX = 'chicagojets_user_profile_';

export function getCachedRosters(): Record<string, SeasonRoster> {
    try {
        const raw = localStorage.getItem(STORAGE_KEY_ROSTERS);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object') return parsed;
        }
    } catch (e) {
        console.warn('Failed to parse cached rosters:', e);
    }
    return {};
}

export function getCachedProfile(email?: string | null): UserProfile | null {
    if (!email) return null;
    try {
        const raw = localStorage.getItem(`${STORAGE_KEY_PROFILE_PREFIX}${email.toLowerCase()}`);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object') return parsed;
        }
    } catch (e) {
        console.warn('Failed to parse cached profile:', e);
    }
    return null;
}

export function setCachedRosters(rosters: Record<string, SeasonRoster>) {
    try {
        localStorage.setItem(STORAGE_KEY_ROSTERS, JSON.stringify(rosters));
    } catch (e) {
        console.warn('Failed to cache rosters:', e);
    }
}

export function setCachedProfile(email: string | null | undefined, profile: UserProfile | null) {
    if (!email) return;
    const key = `${STORAGE_KEY_PROFILE_PREFIX}${email.toLowerCase()}`;
    try {
        if (profile) {
            localStorage.setItem(key, JSON.stringify(profile));
        } else {
            localStorage.removeItem(key);
        }
    } catch (e) {
        console.warn('Failed to cache profile:', e);
    }
}

export const RosterProvider: React.FC<{ 
    user: User | null; 
    isAuthReady?: boolean; 
    children: ReactNode 
}> = ({ user, isAuthReady = true, children }) => {
    const userEmail = user?.email?.toLowerCase();

    const [rosters, setRosters] = useState<Record<string, SeasonRoster>>(() => getCachedRosters());
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAuthorized, setIsAuthorized] = useState<boolean | null>(() => {
        const cached = getCachedProfile(userEmail);
        return cached ? cached.isAuthorized : null;
    });
    const [userProfile, setUserProfile] = useState<UserProfile | null>(() => getCachedProfile(userEmail));

    // Sync cached state if user changes before fetch completes
    useEffect(() => {
        if (userEmail) {
            const cached = getCachedProfile(userEmail);
            if (cached) {
                setUserProfile(cached);
                setIsAuthorized(cached.isAuthorized);
            }
        }
    }, [userEmail]);

    const loadRostersAndProfile = useCallback(async () => {
        // Do not attempt to hit protected endpoints if Firebase Auth has not resolved yet
        if (!isAuthReady) {
            return;
        }

        if (user && !(user as any).isPlaceholder) {
            setActiveUser(user);
        }

        if (!userEmail) {
            setRosters({});
            setUserProfile(null);
            setIsAuthorized(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Fetch user profile and roster data concurrently with valid Firebase ID token
            const [profile, loadedRosters] = await Promise.all([
                fetchUserProfile(),
                fetchRosters()
            ]);

            setUserProfile(profile);
            setRosters(loadedRosters);
            setIsAuthorized(profile.isAuthorized);
            setCachedProfile(userEmail, profile);
            setCachedRosters(loadedRosters);
        } catch (err: any) {
            console.error("Error loading roster/auth data from backend:", err);
            const isForbidden = (ApiError && err instanceof ApiError && err.status === 403) || err?.status === 403;
            if (isForbidden) {
                setIsAuthorized(false);
                setError("Access denied. Your email is not registered on any team roster.");
                setCachedProfile(userEmail, null);
            } else {
                // If we already have cached profile or rosters, avoid wiping out authorization
                const hasExistingData = Boolean(getCachedProfile(userEmail) || Object.keys(getCachedRosters()).length > 0);
                if (!hasExistingData) {
                    setError(err.message || "Failed to load roster data.");
                }
            }
        } finally {
            setIsLoading(false);
        }
    }, [user, userEmail, isAuthReady]);

    useEffect(() => {
        loadRostersAndProfile();
    }, [loadRostersAndProfile]);

    const getRoster = useCallback((seasonKey: string): SeasonRoster | null => {
        return rosters[seasonKey] || null;
    }, [rosters]);

    const userEmailNorm = (userEmail || user?.email || '').trim().toLowerCase();
    const isAdmin = Boolean(userProfile?.isAdmin || userEmailNorm === 'bwuest@gmail.com');
    const isTeamManager = Boolean(userProfile?.isTeamManager);

    return (
        <RosterContext.Provider
            value={{
                rosters,
                isLoading,
                error,
                isAuthorized,
                isTeamManager,
                isAdmin,
                userProfile,
                isAuthReady,
                getRoster,
                refetchRosters: loadRostersAndProfile
            }}
        >
            {children}
        </RosterContext.Provider>
    );
};

export const useRoster = (): RosterContextType => {
    const context = useContext(RosterContext);
    if (!context) {
        throw new Error('useRoster must be used within a RosterProvider');
    }
    return context;
};
